import { useEffect, useId, useMemo, useRef } from 'react'
import * as Mtds from '@mattilsynet/design/react'
import * as mtds from '@mattilsynet/design'
import type { LegislationDocument } from '../../types/types'
import { prepareLegislationHtml } from '../../utils/legislationHtml'

type Props = {
  document: LegislationDocument
  selected: string[]
  onChange?: (ids: string[]) => void
}

/** Displays the backend's transient, allowlisted selection preview, never raw upstream HTML. */
export function LegislationText({
  document,
  selected,
  onChange
}: Readonly<Props>) {
  const ref = useRef<HTMLDivElement>(null)
  const prefix = useId()
  // Law mutates checkbox attributes. Keep its HTML identity stable across selection renders.
  const html = useMemo(
    () => ({
      __html: prepareLegislationHtml(document.html, prefix)
    }),
    [document.html, prefix]
  )

  useEffect(() => {
    mtds.setLawChecked(selected, ref.current)
    ref.current?.querySelectorAll('button').forEach((button) => {
      button.disabled =
        !onChange || !document.availablePartIds.includes(button.value)
    })
  }, [selected, document.availablePartIds, html, onChange])

  return (
    <Mtds.Law
      ref={ref}
      data-variant={onChange ? undefined : 'readonly'}
      dangerouslySetInnerHTML={html}
      onClick={(event) => {
        const button =
          event.target instanceof Element
            ? event.target.closest('button')
            : null
        if (
          button &&
          onChange &&
          document.availablePartIds.includes(button.value)
        ) {
          onChange(
            selected.includes(button.value)
              ? selected.filter((id) => id !== button.value)
              : [...selected, button.value]
          )
        }
      }}
    />
  )
}
